import React, { useEffect, useMemo, useState } from 'react'
import {
	Typography,
	Space,
	Form,
	Button,
	Notification,
	Layout,
	Nav,
	RadioGroup,
	Radio,
	Card,
	PinCode,
	withField
} from '@douyinfe/semi-ui'
import userStore from '@src/store/common/user'
import './login.scss'
import { useNavigate } from 'react-router-dom'
import { login } from '@src/api/user/login'
import systemConfig from '@src/config'
import { setLocalStorage } from '@src/utils/storage'
import { IconFeishuLogo, IconGithubLogo, IconPhone, IconDesktop } from '@douyinfe/semi-icons'
import { IconDarkMode } from '@douyinfe/semi-icons-lab'
import { RiWechatFill, RiShieldUserLine, RiUserLine } from '@remixicon/react'
import useStore from '@src/store/common/global'
import { useIntl } from 'react-intl'
import { useLoading } from '@src/hooks/useLoading'
import { selectLocale, switchMode, textList } from '@src/common'
import { CountdownButton } from '@components/OneCountdownButton'
import { getCommonCode } from '@src/api/common/code'
import codeService from '@src/services/codeService'
import Terminal from './components/Terminal'

const { Title, Paragraph } = Typography

const renderLogo = () => {
	return (
		<img
			className="login-sider-logo"
			src="https://lf3-static.bytednsdoc.com/obj/eden-cn/ptlz_zlp/ljhwZthlaukjlkulzlp/root-web-sites/semi_logo.svg"
			alt="semi_logo"
			style={{ width: 87, height: 31 }}
		/>
	)
}
const { authKey } = systemConfig

const Index: React.FC = () => {
	const { formatMessage: i18nMsg } = useIntl()
	const navigate = useNavigate()
	const fetchUserInfo = userStore((state) => state.fetchUserInfo)
	const userToken = userStore((state) => state.token)
	useEffect(() => {
		if (userToken) {
			navigate('/dashboard/workbench')
		}
	}, [userToken])
	const { loading } = useLoading()
	const loginOptions: {
		type: string
		icon: React.ReactNode
		labelKey: string
		/** 仅 OAuth 类型使用：在 OAuth 卡片中显示的供应商名（用于 i18n 插值） */
		providerName?: string
	}[] = [
		{ type: 'desktop', icon: <IconDesktop size="extra-large" />, labelKey: 'app.system.login.type.account' },
		{ type: 'phone', icon: <IconPhone size="extra-large" />, labelKey: 'app.system.login.type.phone' },
		{
			type: 'github',
			icon: <IconGithubLogo size="extra-large" />,
			labelKey: 'app.system.login.type.github',
			providerName: 'GitHub'
		},
		{
			type: 'feishu',
			icon: <IconFeishuLogo size="extra-large" />,
			labelKey: 'app.system.login.type.feishu',
			providerName: 'Feishu'
		},
		{
			type: 'wechat',
			icon: <RiWechatFill />,
			labelKey: 'app.system.login.type.wechat',
			providerName: 'WeChat'
		}
	]
	const oauthTypes = new Set(['github', 'feishu', 'wechat'])
	const [loginType, setLoginType] = useState<string>(loginOptions[0].type)
	const handleSubmit = async (values: { userName: string; password: string; acceptedTerms: boolean }) => {
		if (!values.userName || !values.password) {
			Notification.error({ content: '请输入用户名和密码', position: 'top', duration: 3 })
			return
		}

		const loginParam = {
			username: values.userName,
			password: values.password,
			acceptedTerms: values.acceptedTerms,
			loginType: 'sys_password'
		}
		const res = await login(loginParam)
		// 登录失败
		const { code, msg, data } = res
		if (code !== 200) {
			Notification.error({ content: msg, position: 'top', duration: 3 })
			return
		}
		await afterLogin(data)
	}
	// 登录成功 进入登录后置操作
	const afterLogin = async (data) => {
		const { token } = data
		setLocalStorage(authKey, token.tokenValue)
		await fetchUserInfo()
		await codeService.initializeCodes()
		//定时刷新
		codeService.startAutoRefresh()
		// 登录成功
		userStore.setState({
			token: token.tokenValue
		})
		Notification.success({
			title: `${userStore.getState().name}`,
			content: i18nMsg({ id: 'app.system.login.welcome' }),
			position: 'top',
			duration: 3
		})
		// 跳转工作台
		navigate('/dashboard/workbench')
	}
	const { Header, Sider, Content } = Layout
	const locale = useStore((state) => state.locale)
	const FormPinCode = withField(PinCode)
	// 每次挂载随机生成 4-6 个静态光晕
	// - 数量 4-6（每次随机）
	// - 位置 10-90%（避免贴边被裁切）
	// - 大小 300-550px
	// - alpha 0.4-0.7
	// - 颜色从 4 个冷色调随机（cyan / indigo / violet / blue）
	// useMemo([]) 只算一次，挂载时 ~1ms，运行时零开销
	const glowBackground = useMemo(() => {
		const COLORS = [
			'34, 211, 238', // cyan
			'99, 102, 241', // indigo
			'139, 92, 246', // violet
			'59, 130, 246' // blue
		]
		const count = 4 + Math.floor(Math.random() * 3) // 4-6
		return Array.from({ length: count }, () => {
			const size = Math.round(300 + Math.random() * 250)
			const x = (10 + Math.random() * 80).toFixed(1)
			const y = (10 + Math.random() * 80).toFixed(1)
			const alpha = (0.4 + Math.random() * 0.3).toFixed(2)
			const color = COLORS[Math.floor(Math.random() * COLORS.length)]
			return `radial-gradient(circle ${size}px at ${x}% ${y}%, rgba(${color}, ${alpha}), transparent 65%)`
		}).join(', ')
	}, [])
	return (
		<Layout className="components-layout-demo">
			<Sider className="login-sider">
				<div className="login-sider-bg" aria-hidden />
				<div className="login-sider-grid" aria-hidden />
				<div className="login-sider-glow" aria-hidden style={{ background: glowBackground }} />
				<Terminal />
			</Sider>
			<Layout>
				<Header >
					<Nav
						mode="horizontal"
						className="layout-header"
						style={{ borderBottom: 'none' }}
						footer={
							<>
								<Button
									theme="borderless"
									icon={<IconDarkMode size="extra-large" />}
									style={{
										color: 'var(--semi-color-text-2)',
										marginRight: '12px'
									}}
									onClick={(e: React.MouseEvent) => switchMode(e.clientX, e.clientY)}
								/>
								<RadioGroup type="button" defaultValue={locale} style={{ marginLeft: '20px' }}>
									<Radio value={'zh_CN'} onChange={() => selectLocale('zh_CN')}>
										{i18nMsg({ id: 'app.common.language.chinese' })}
									</Radio>
									<Radio value={'en_GB'} onChange={() => selectLocale('en_GB')}>
										EN
									</Radio>
								</RadioGroup>
							</>
						}
					/>
				</Header>
				<Content className="login-content">
					<Card style={{ width: '100%', height: '100%', borderRadius: 0 }} className="login-form">
						<Form onSubmit={(values) => handleSubmit(values)} style={{ width: 400 }}>
							{({ formState, values, formApi }) => (
								<>
									<div className="login-form-stage">
										{loginType === 'desktop' && (
											<>
												<div className="login-demo-accounts">
													<div className="login-demo-accounts-title">
														<span>{i18nMsg({ id: 'app.system.login.demo.title' })}</span>
														<span className="login-demo-accounts-hint">
															{i18nMsg({ id: 'app.system.login.demo.hint' })}
														</span>
													</div>
													<div className="login-demo-cards">
														{[
															{
																key: 'admin',
																icon: <RiShieldUserLine />,
																labelKey: 'app.system.login.demo.admin',
																descKey: 'app.system.login.demo.adminDesc',
																username: 'admin',
																password: '123456'
															},
															{
																key: 'test',
																icon: <RiUserLine />,
																labelKey: 'app.system.login.demo.test',
																descKey: 'app.system.login.demo.testDesc',
																username: 'test',
																password: '123456'
															}
														].map((card) => {
															// 选中态：username 匹配即高亮（手动改 username 会自动取消）
															const isActive = values.userName === card.username
															return (
																<div
																	key={card.key}
																	role="button"
																	aria-pressed={isActive}
																	tabIndex={0}
																	className={`login-demo-card${isActive ? ' active' : ''}`}
																	onClick={() => {
																		formApi.setValue('userName', card.username)
																		formApi.setValue('password', card.password)
																	}}
																	onKeyDown={(e) => {
																		if (e.key === 'Enter' || e.key === ' ') {
																			e.preventDefault()
																			formApi.setValue('userName', card.username)
																			formApi.setValue('password', card.password)
																		}
																	}}
																>
																	<div className="login-demo-card-icon">{card.icon}</div>
																	<div className="login-demo-card-label">{i18nMsg({ id: card.labelKey })}</div>
																	<div className="login-demo-card-desc">{i18nMsg({ id: card.descKey })}</div>
																</div>
															)
														})}
													</div>
												</div>
												<Form.Input
													field="userName"
													label={i18nMsg({ id: 'app.system.login.username' })}
													style={{ width: '100%' }}
													placeholder={i18nMsg({ id: 'app.system.login.usernamePlaceholder' })}
													rules={[{ required: true, message: '请输入用户名' }]}
												/>
												<Form.Input
													mode="password"
													field="password"
													label={i18nMsg({ id: 'app.system.login.password' })}
													style={{ width: '100%' }}
													placeholder={i18nMsg({ id: 'app.system.login.passwordPlaceholder' })}
													rules={[{ required: true, message: '请输入密码' }]}
												/>
												<Form.Checkbox field="acceptedTerms" noLabel>
													{i18nMsg({ id: 'app.system.login.privacy' })}
												</Form.Checkbox>
												<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
													<p>
														<span>{i18nMsg({ id: 'app.system.login.or' })}</span>
														<Button
															theme="borderless"
															style={{ color: 'var(--semi-color-primary)', marginLeft: 10, cursor: 'pointer' }}
														>
															{i18nMsg({ id: 'app.system.login.registry' })}
														</Button>
													</p>
													<Button loading={loading} disabled={!values.acceptedTerms} htmlType="submit" type="primary">
														{i18nMsg({ id: 'app.system.login.login' })}
													</Button>
												</div>
											</>
										)}
										{loginType === 'phone' && (
											<>
												<Form.Input
													field="phone"
													label={i18nMsg({ id: 'app.system.login.phone' })}
													style={{ width: '100%' }}
													placeholder={i18nMsg({ id: 'app.system.login.phonePlaceholder' })}
													suffix={
														<CountdownButton
															theme="borderless"
															type="primary"
															defaultButtonText={i18nMsg({ id: 'app.system.login.getpincode' })}
															duration={10}
															onClick={(start) => {
																start()
																// sendCode().then(() => start());
															}}
														/>
														// <Button theme="borderless" type="primary">
														// 	获取验证码
														// </Button>
													}
												></Form.Input>
												<FormPinCode
													field="pincode"
													label={i18nMsg({ id: 'app.system.login.pincode' })}
													style={{ justifyContent: 'space-between' }}
													onChange={() => {
														console.log()
													}}
												></FormPinCode>
												<Form.Checkbox field="acceptedTerms" noLabel>
													{i18nMsg({ id: 'app.system.login.privacy' })}
												</Form.Checkbox>
												<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
													<p>
														<span>{i18nMsg({ id: 'app.system.login.or' })}</span>
														<Button
															theme="borderless"
															style={{ color: 'var(--semi-color-primary)', marginLeft: 10, cursor: 'pointer' }}
														>
															{i18nMsg({ id: 'app.system.login.registry' })}
														</Button>
													</p>
													<Button
														loading={loading}
														disabled={!(values.acceptedTerms && (loginType != 'phone' || values.pincode?.length === 6))}
														htmlType="submit"
														type="primary"
													>
														{i18nMsg({ id: 'app.system.login.login' })}
													</Button>
												</div>
											</>
										)}
										{oauthTypes.has(loginType) &&
											(() => {
												const option = loginOptions.find((o) => o.type === loginType)
												if (!option) return null
												const provider = option.providerName ?? ''
												// 微信扫码登录：渲染真实二维码，正中叠一个 WeChat logo 当品牌锚点
												if (loginType === 'wechat') {
													return (
														<div className="login-oauth-card">
															<div className="login-oauth-icon">{option.icon}</div>
															<div className="login-oauth-title">
																{i18nMsg({ id: 'app.system.login.signInWith' }, { provider })}
															</div>
															<div className="login-oauth-wechat-qr">
																<img
																	src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
																		'http://www.baidu.com'
																	)}&size=200x200`}
																	alt="WeChat QR"
																	width={200}
																	height={200}
																/>
																<div className="login-oauth-wechat-qr-logo">
																	<RiWechatFill />
																</div>
															</div>
															<div className="login-oauth-desc">
																{i18nMsg({ id: 'app.system.login.wechat.scanHint' })}
															</div>
														</div>
													)
												}
												return (
													<div className="login-oauth-card">
														<div className="login-oauth-icon">{option.icon}</div>
														<div className="login-oauth-title">
															{i18nMsg({ id: 'app.system.login.signInWith' }, { provider })}
														</div>
														<div className="login-oauth-desc">
															{i18nMsg({ id: 'app.system.login.oauthDesc' }, { provider })}
														</div>
														<Button type="primary" theme="solid" size="large" block icon={option.icon}>
															{i18nMsg({ id: 'app.system.login.continue' })}
														</Button>
													</div>
												)
											})()}
									</div>
									<div
										className="login-type-switcher"
										role="tablist"
										aria-label={i18nMsg({ id: 'app.system.login.type' })}
									>
										{loginOptions.map((option) => {
											const isActive = loginType === option.type
											return (
												<div
													key={option.type}
													role="tab"
													aria-selected={isActive}
													tabIndex={0}
													className={`login-type-tab${isActive ? ' active' : ''}`}
													onClick={() => setLoginType(option.type)}
													onKeyDown={(e) => {
														if (e.key === 'Enter' || e.key === ' ') {
															e.preventDefault()
															setLoginType(option.type)
														}
													}}
												>
													<span className="login-type-tab-icon">{option.icon}</span>
													<span className="login-type-tab-label">{i18nMsg({ id: option.labelKey })}</span>
												</div>
											)
										})}
									</div>
								</>
							)}
						</Form>
					</Card>
				</Content>
			</Layout>
		</Layout>
	)
}

export default Index
